import { useMemo, useRef, useState } from 'react';
import { FeeWaiverDocument, FeeWaiverSchema, FormActionsStatus, FormNames } from '@youscience/commonapp-service-common';
import { ThemeProvider } from '@mui/material';
import { brightpathTheme } from '@youscience/theme';

import useRecommenderWorkspaceStore from '@store/RecommenderWorkspaceStore';
import useNotificationStore from '@store/NotificationStore';

import useRecommenderStore from '@store/RecommenderStore';

import { TabSections } from '@interfaces/recommenderWorkspace';

import { useUserProfile } from '@hooks/useUserProfile';

import { FormState } from '@constants/commonApp/types';

import EconomicNeed, { EconomicNeedFields } from './components/EconomicNeed';
import FormHeader from '../../common/FormHeader';
import FormFooter from '../../common/FormFooter';
import {
  checkCounselorProfileValidity,
  checkFormValidity,
  PREVIEW_ERROR_NOTIFICATION,
  REVIEW_NOT_READY_ERROR_NOTIFICATION,
  onSaveErrorNotification,
  onSaveSuccessNotification,
  onSubmitSuccessNotification,
  onUnsubmitSuccessNotification,
} from '../../common/formUtils';
import ReviewPdf, { ReviewTypes } from '../../common/ReviewPdf';
import Submissions from '../../common/Submissions';
import { useApplicantForm } from '@pages/StudentDetailsPage/hooks/useApplicantForm';
import { FormAvailabilityStatus, FormCompletionStatus } from '../../common/enums';
import { formatDateWithFallback } from '@utils/formatter';

const formName = FormNames.FEE_WAIVER;

export const FeeWaiver = () => {
  const { notify } = useNotificationStore((state) => state);
  const { recommenderProfile } = useRecommenderStore((store) => store);
  const { userId } = useUserProfile();
  const feeWaiver = useApplicantForm<FeeWaiverDocument>(formName);
  const initialFormState = useMemo(
    () => ({
      [TabSections.EconomicNeed]: feeWaiver.content?.economicNeed,
    }),
    [feeWaiver.content],
  );
  const form = useRef(initialFormState);
  const [formErrors, setFormErrors] = useState({});
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [expandedSection, setExpandedSection] = useState<TabSections | null>(TabSections.EconomicNeed);
  const [reviewType, setReviewType] = useState<ReviewTypes>(ReviewTypes.PREVIEW_FORM);

  const isSubmitted =
    feeWaiver.metadata.completionStatus === FormCompletionStatus.SUBMITTED ||
    feeWaiver.metadata.completionStatus === FormCompletionStatus.DOWNLOADED;
  const isSubmitDisabled = feeWaiver.metadata.availabilityStatus !== FormAvailabilityStatus.AVAILABLE;
  const { applicantId, recommenderId } = feeWaiver.metadata;

  const toggleExpandedSelection = (section: TabSections) => {
    if (expandedSection === section) {
      setExpandedSection(null);
    } else {
      setExpandedSection(section);
    }
  };

  const onFormChangeHandler =
    <K,>(section: TabSections) =>
    (sectionFormState: FormState<K>) => {
      form.current = { ...form.current, [section]: sectionFormState.data };
    };

  const validateForm = () => {
    const formValid = checkFormValidity({ ...form.current, applicantId, recommenderId }, FeeWaiverSchema);
    const profileValid = checkCounselorProfileValidity(recommenderProfile);

    return {
      form: !formValid,
      recommenderProfile: !profileValid.personal,
      schoolProfile: !profileValid.school,
    };
  };

  const handleSaveForLater = async (isHideNotification?: boolean) => {
    await feeWaiver.save({ ...form.current, applicantId, recommenderId });

    if (!isHideNotification) {
      feeWaiver.isError ? onSaveErrorNotification() : onSaveSuccessNotification(formName);
    }
  };

  const previewForm = async () => {
    if (isSubmitted) {
      setIsPreviewOpen(true);
      await feeWaiver.getSubmitted({ applicantId, recommenderId });
    } else {
      await handleSaveForLater(true);

      const formErrors = validateForm();
      setFormErrors(formErrors);
      setIsPreviewOpen(true);
      const hasFormErrors = Object.values(formErrors).some(Boolean);

      if (!hasFormErrors) {
        await feeWaiver.preview({ ...form.current, applicantId, recommenderId });
      }
    }

    useRecommenderWorkspaceStore.subscribe((state) => {
      if (state.isError) {
        notify(isSubmitted ? REVIEW_NOT_READY_ERROR_NOTIFICATION : PREVIEW_ERROR_NOTIFICATION);
        setIsPreviewOpen(false);
      }
    });
  };

  const submitForm = async (signature: string, signedDate: string) => {
    await feeWaiver.submit({
      applicantId,
      recommenderId,
      counselorApproval: { isReviewed: true, isAffirmed: true, signature, signedDate, userId: userId as string },
    });

    feeWaiver.isError ? onSaveErrorNotification() : onSubmitSuccessNotification(formName);
  };

  const handleOnSubmit = () => {
    setReviewType(ReviewTypes.REVIEW_AND_SUBMIT);
    void previewForm();
  };

  const handleOnPreview = async () => {
    setReviewType(isSubmitted ? ReviewTypes.REVIEW_FORM : ReviewTypes.PREVIEW_FORM);
    void previewForm();
  };

  const handleOnUnsubmit = async () => {
    await feeWaiver.unsubmit({ applicantId, recommenderId });

    feeWaiver.isError ? onSaveErrorNotification() : onUnsubmitSuccessNotification(formName);
  };

  if (!feeWaiver.isReady) {
    return null;
  }

  return (
    <ThemeProvider theme={brightpathTheme}>
      <FormHeader
        name="Fee waiver"
        tooltipTitle="If a student requests an application fee waiver, this form affirms that the student is eligible."
        deadline={formatDateWithFallback(feeWaiver.metadata.deadline)}
        status={feeWaiver.metadata?.completionStatus || FormActionsStatus.NEW}
        onPreviewClick={handleOnPreview}
        onSaveForLaterClick={handleSaveForLater}
        onSubmitClick={handleOnSubmit}
        onUnsubmitClick={handleOnUnsubmit}
        areAllActionsDisabled={feeWaiver.isSavingForm}
        isSubmitDisabled={isSubmitDisabled}
        isFormSumbitted={isSubmitted}
        formName={formName}
      />

      {isSubmitted ? (
        <Submissions formName={formName} onSubmit={handleOnSubmit} />
      ) : (
        <EconomicNeed
          economicNeed={feeWaiver.content?.economicNeed}
          isExtended={expandedSection === TabSections.EconomicNeed}
          onExpand={() => toggleExpandedSelection(TabSections.EconomicNeed)}
          onFormChange={onFormChangeHandler<EconomicNeedFields>(TabSections.EconomicNeed)}
          isSaved={feeWaiver.sectionIsSaved.economicNeed}
        />
      )}

      <FormFooter
        onPreviewClick={handleOnPreview}
        onSaveForLaterClick={handleSaveForLater}
        onSubmitClick={handleOnSubmit}
        onUnsubmitClick={handleOnUnsubmit}
        areAllActionsDisabled={feeWaiver.isSavingForm}
        isSubmitDisabled={isSubmitDisabled}
        isFormSumbitted={isSubmitted}
        formName={formName}
      />

      {isPreviewOpen ? (
        <ReviewPdf
          title="Fee Waiver"
          subtitle="Please review the PDF preview of your form. Scroll to the bottom if necessary to confirm and continue."
          isOpen={isPreviewOpen}
          isLoading={feeWaiver.isLoadingPreview}
          reviewType={reviewType}
          pdf={feeWaiver.previewPdfURL}
          onClose={() => setIsPreviewOpen(false)}
          onContinue={(signature?: string, date?: string) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            if (reviewType === ReviewTypes.REVIEW_AND_SUBMIT) submitForm(signature!, date!);
          }}
          errors={formErrors}
        />
      ) : null}
    </ThemeProvider>
  );
};
